import { useEffect } from 'react'

/**
 * Raccourcis clavier pour le studio video.
 *
 * Conventions (volontairement simples, pas Hudl/JKL) :
 *   Espace        → play / pause
 *   Fleche gauche → -10s
 *   Fleche droite → +10s
 *
 * On ignore les evenements quand le focus est dans un input/textarea
 * pour ne pas casser la saisie texte.
 *
 * @param {React.RefObject<HTMLVideoElement>} videoRef
 * @param {boolean} enabled - desactiver le hook proprement (modal ouverte, etc.)
 */
export default function useVideoKeyboard(videoRef, enabled = true) {
  useEffect(() => {
    if (!enabled) return

    const isTypingTarget = (el) => {
      if (!el) return false
      const tag = (el.tagName || '').toUpperCase()
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if (el.isContentEditable) return true
      return false
    }

    const handler = (e) => {
      if (isTypingTarget(e.target)) return
      const video = videoRef.current
      if (!video) return

      switch (e.code) {
        case 'Space': {
          e.preventDefault()
          if (video.paused) video.play().catch(() => {})
          else video.pause()
          break
        }
        case 'ArrowLeft': {
          e.preventDefault()
          video.currentTime = Math.max(0, video.currentTime - 10)
          break
        }
        case 'ArrowRight': {
          e.preventDefault()
          const target = video.currentTime + 10
          video.currentTime = Number.isFinite(video.duration)
            ? Math.min(video.duration, target)
            : target
          break
        }
        default:
          break
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [videoRef, enabled])
}
